export type AdminIndividualRankingRow = {
  userId: string;
  rank: number | null;
  displayName: string;
  teamNumber: number | null;
  individualScore: number;
  updatedAt: number;
};

export type AdminTeamRankingRow = {
  teamId: string;
  rank: number;
  teamNumber: number;
  teamScore: number;
};

export type AdminLeaderboardMeta = {
  ranksUpdatedAt: number;
  userCount: number;
};

export type AdminTeamLeaderboardMeta = {
  ranksUpdatedAt: number;
  teamCount: number;
};

const asInt = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

export const parseTeamLeaderboardMeta = (
  value: unknown
): AdminTeamLeaderboardMeta | null => {
  const raw = asRecord(value);
  if (!raw) {
    return null;
  }
  return {
    ranksUpdatedAt: asInt(raw.ranksUpdatedAt) ?? 0,
    teamCount: asInt(raw.teamCount) ?? 0,
  };
};

export const parseIndividualLeaderboardMeta = (
  value: unknown
): AdminLeaderboardMeta | null => {
  const raw = asRecord(value);
  if (!raw) {
    return null;
  }
  return {
    ranksUpdatedAt: asInt(raw.ranksUpdatedAt) ?? 0,
    userCount: asInt(raw.userCount) ?? 0,
  };
};

const parseIndividualRow = (
  userId: string,
  value: unknown
): AdminIndividualRankingRow | null => {
  const raw = asRecord(value);
  if (!raw || !userId) {
    return null;
  }
  const individualScore = asInt(raw.individualScore);
  if (individualScore == null) {
    return null;
  }
  return {
    userId,
    rank: asInt(raw.rank),
    displayName: typeof raw.displayName === "string" ? raw.displayName : "",
    teamNumber: asInt(raw.teamNumber),
    individualScore,
    updatedAt: asInt(raw.updatedAt) ?? 0,
  };
};

export const parseIndividualTop = (
  value: unknown
): AdminIndividualRankingRow[] => {
  if (value == null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      const raw = asRecord(item);
      const userId = raw?.userId;
      if (typeof userId !== "string") {
        return [];
      }
      const row = parseIndividualRow(userId, item);
      return row ? [row] : [];
    });
  }

  const raw = asRecord(value);
  if (!raw) {
    return [];
  }

  const byIndex = new Map<number, AdminIndividualRankingRow>();
  for (const [key, item] of Object.entries(raw)) {
    const index = Number.parseInt(key, 10);
    const nested = asRecord(item);
    const userId = nested?.userId;
    if (!Number.isFinite(index) || typeof userId !== "string") {
      continue;
    }
    const row = parseIndividualRow(userId, item);
    if (row) {
      byIndex.set(index, row);
    }
  }
  return [...byIndex.keys()]
    .sort((a, b) => a - b)
    .map((index) => byIndex.get(index)!);
};

export const parseTeamLeaderboardRows = (
  value: unknown
): AdminTeamRankingRow[] => {
  const raw = asRecord(value);
  if (!raw) {
    return [];
  }

  const rows: AdminTeamRankingRow[] = [];
  for (const [teamId, item] of Object.entries(raw)) {
    if (teamId === "meta") {
      continue;
    }
    const nested = asRecord(item);
    if (!nested) {
      continue;
    }
    rows.push({
      teamId,
      rank: asInt(nested.rank) ?? 0,
      teamNumber: asInt(nested.teamNumber) ?? 0,
      teamScore: asInt(nested.teamScore) ?? 0,
    });
  }

  rows.sort((a, b) => {
    if (a.rank !== b.rank && a.rank > 0 && b.rank > 0) {
      return a.rank - b.rank;
    }
    if (b.teamScore !== a.teamScore) {
      return b.teamScore - a.teamScore;
    }
    return a.teamNumber - b.teamNumber;
  });
  return rows;
};
